import { modalController } from '@ionic/core';
import { Component, Element, Event, EventEmitter, forceUpdate, Fragment, h, Listen, Prop, State } from '@stencil/core';
import { FilterDoc, TaskDoc } from 'src/components';
import { ScheduleService } from 'src/global/service/ScheduleService';
import { ScheduleData } from './model/ScheduleData';
import { User } from '../../global/models';
import { PermissionsSet } from '../../global/models/User';
import { RequestAccess } from '../concourse-login/share-link/request-access';
import { Task } from './model/Task';

const DEFAULT_SEARCH_DATA = {
  name: '',
  searchTerm: '',
  orConditions: [],
  startDate: null,
  finishDate: null,
  dateFilterType: 'none',
  lookaheadStartType: 'dataDate',
  lookaheadWeeks: 6,
};

@Component({
  tag: 'task-selector',
  styleUrl: 'task-selector.css',
})
export class TaskSelector {
  static async presentModal(projectId: string, focusedTaskId: number = null): Promise<TaskDoc | null> {
    const modal: any = await modalController.create({
      component: 'task-selector',
      componentProps: {
        projectId,
        focusedTaskId,
      },
      showBackdrop: true,
      backdropDismiss: false,
      cssClass: 'concourse-modal modal-size-90',
    });

    return new Promise<TaskDoc | null>(resolve => {
      modal.addEventListener('selectTask', async (e: CustomEvent) => {
        resolve(e.detail as TaskDoc);
        modal.dismiss();
      });
      modal.addEventListener('dismiss', () => {
        resolve(null);
        modal.dismiss();
      });
      modal.present();
    });
  }

  @Prop() projectId: string;
  @Prop() focusedTaskId: number;

  @State() scheduleData: ScheduleData;
  @State() searchOpen: boolean;
  @State() tasksHilighted: Task[] = [];
  @State() taskFramed: Task;
  @State() loadingMessage: string;
  @State() errorMessage: string;

  @Event() dismiss;
  @Event() selectTask: EventEmitter<TaskDoc>;

  @Element() el: HTMLElement;

  private readonly urlBase: string = 'schedule';
  private permissions: PermissionsSet;
  private doubleHilightTimeout: ReturnType<typeof setTimeout> = null;
  private prevHilightedTask: Task = null;

  get user(): User {
    return globalThis.user;
  }

  async componentDidLoad() {
    this.permissions = this.user.getSchedulePermissions(this.projectId);
    if (!this.permissions.canRead) {
      RequestAccess.presentModal(this.projectId, 'Schedules', this.permissions.readRole);
      this.dismiss.emit();
      return;
    }

    this.scheduleData = new ScheduleData(this.projectId);
    this.scheduleData.onlyTasks = true;
    this.scheduleData.addEventListener(ScheduleData.LOADING_MESSAGE, (e: CustomEvent) => {
      this.loadingMessage = e.detail.message;
    });
    this.scheduleData.addEventListener(ScheduleData.LOAD_COMPLETE, (e: CustomEvent) => {
      // Find task by WBS code
      const taskFocused = this.scheduleData.taskMap.get(this.focusedTaskId);
      if (taskFocused) {
        this.tasksHilighted = [taskFocused];
        this.taskFramed = taskFocused.parent;
      }
      this.loadingMessage = null;
    });

    this.loadingMessage = 'Loading schedule...';
    this.scheduleData.loadScheduleData();
  }

  disconnectedCallback() {
    this.scheduleData?.dispose();
    this.scheduleData = null;
  }

  @Listen('frameTask')
  handleSelectTask(event: CustomEvent<Task>) {
    event.stopPropagation();
    this.taskFramed = event.detail;
  }

  @Listen('hilightTask')
  handleHilightTask(event: CustomEvent<Task>) {
    const task = event.detail;
    if (task) {
      if (this.prevHilightedTask == task) {
        if (!task.expanded && task.canExpand) {
          task.expanded = true;
          this.scheduleData.saveTasks([task.doc]);
        } else if (task.isContainer) {
          this.taskFramed = task == this.taskFramed ? task.parent : task;
        }
      } else {
        this.tasksHilighted = [task];
        this.prevHilightedTask = task;
        if (this.doubleHilightTimeout) clearTimeout(this.doubleHilightTimeout);
        this.doubleHilightTimeout = setTimeout(() => {
          console.log('doubleHilightTimeout', this.prevHilightedTask);
          this.prevHilightedTask = null;
        }, 1000);
      }
    }
  }

  get taskHilightedOrSelected(): Task {
    return this.tasksHilighted[this.tasksHilighted.length - 1] || this.taskFramed;
  }

  get canSelectTask(): boolean {
    return !!this.taskHilightedOrSelected?.isLeaf;
  }

  onSelectButtonClick() {
    if (!this.canSelectTask) return;
    this.selectTask.emit(this.taskHilightedOrSelected.doc);
  }

  closeSelector() {
    this.dismiss.emit();
  }

  async onClearFilter(filter: FilterDoc) {
    this.scheduleData.filterIds = this.scheduleData.filterIds.replace(filter._id, '');
  }

  async onApplyFilter(filter: FilterDoc) {
    this.scheduleData.filterIds = this.scheduleData.filterIds ? this.scheduleData.filterIds + ',' + filter._id : filter._id;
  }

  closeSearch() {
    this.searchOpen = false;
    forceUpdate(this);
  }

  render() {
    return (
      <Fragment>
        <modal-header modalTitle="Task Selector" onDismiss={() => this.dismiss.emit()}>
          <ion-button onClick={() => (this.searchOpen = !this.searchOpen)}>
            <ion-icon
              slot="icon-only"
              class={this.searchOpen ? 'header-icon-selected' : 'header-icon'}
              name={this.scheduleData?.activeFilters.length > 0 ? 'funnel-outline' : 'search-outline'}
            ></ion-icon>
          </ion-button>
        </modal-header>
        {this.errorMessage ? (
          <div class="center-content" style={{ height: '100%' }}>
            <ion-text color="danger">{this.errorMessage}</ion-text>
          </div>
        ) : this.loadingMessage ? (
          <div class="center-content" style={{ height: '100%' }}>
            <ion-spinner />
            <p style={{ marginLeft: '10px' }}>{this.loadingMessage}</p>
          </div>
        ) : (
          this.scheduleData &&
          this.scheduleData.tasks.length > 0 && (
            <ion-content>
              <view-canvas
                key={`task-selector-${this.scheduleData?.filterIds}`}
                projectId={this.projectId}
                scheduleData={this.scheduleData}
                taskFramed={this.taskFramed}
                zoomMode={true}
                tasksHilighted={this.tasksHilighted}
                style={{ display: 'block', width: this.searchOpen ? 'calc(100% - 300px)' : '100%', height: '100%' }}
                showSlack={false}
              ></view-canvas>
              <schedule-search
                class="infopanel"
                style={{ display: this.searchOpen ? 'block' : 'none' }}
                projectId={this.projectId}
                scheduleData={this.scheduleData}
                filtersApplied={this.scheduleData.activeFilters}
                onApplyFilters={e => this.onApplyFilter(e.detail)}
                onClearFilters={e => this.onClearFilter(e.detail)}
                onDismiss={() => (this.searchOpen = false)}
              />
            </ion-content>
          )
        )}
        <ion-footer>
          <ion-toolbar>
            {this.taskHilightedOrSelected && (
              <ion-button class="select-button" slot="end" shape="round" disabled={!this.canSelectTask} onClick={() => this.onSelectButtonClick()}>
                Select {this.taskHilightedOrSelected.name}
              </ion-button>
            )}
          </ion-toolbar>
        </ion-footer>
      </Fragment>
    );
  }
}
